// SupportErrorBoundary (Story #4613, Stream 8b) — the error-report fallback path.
// When a wrapped screen throws, componentDidCatch writes a SANITISED crash record
// to sessionStorage and renders a degraded-but-safe fallback that lets the user
// turn the crash into a bug report (deep-links to /support?report=1). The fallback
// null-guards everything and NEVER throws a second time inside the terminal
// boundary — a missing/unparseable record still renders a working "Report" link.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { writeCrashReport } from './support/crashReport'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export default class SupportErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Record for the report handoff. Wrapped in its own try (writeCrashReport is
    // already defensive) so logging can never re-throw inside the boundary.
    try {
      writeCrashReport(error, info?.componentStack ?? '')
    } catch {
      /* never throw from the terminal boundary */
    }
  }

  private handleReload = () => {
    this.setState({ error: null })
    try {
      window.location.reload()
    } catch {
      /* non-fatal */
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children

    // Degraded-but-safe fallback. No app state is read here beyond the error's own
    // message, so it renders even when everything else is broken.
    const message = String(this.state.error?.message || 'An unexpected error occurred')
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center p-8 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-2xl text-rose-500">!</div>
        <h2 className="mb-2 text-lg font-semibold text-iflow-900">Something went wrong</h2>
        <p className="mb-5 max-w-md text-sm text-slate-500">{message}</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-lg bg-iflow-500 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-600"
          >
            Reload page
          </button>
          {/* A hard anchor (not SPA nav) so the report link works even if routing is
              part of what broke. The Support page reads the crash record on load. */}
          <a
            href="/support?report=1"
            className="rounded-lg border border-iflow-200 px-4 py-2 text-sm font-medium text-iflow-700 hover:bg-iflow-50"
          >
            Report this problem
          </a>
        </div>
      </div>
    )
  }
}
